'use client'

import { useEffect, useRef, useSyncExternalStore } from 'react'
import { usePathname } from 'next/navigation'
import { useDisclosure } from '@mantine/hooks'
import { Anchor, Box, Group, Stack, Text, ThemeIcon, UnstyledButton } from '@mantine/core'
import classes from './topBar.module.css'
import { IconUser } from '@tabler/icons-react'
import CoordinatorDialog from '@/tenants/walmart/components/CoordinatorDialog/CoordinatorDialog'
import { WALMART_CHROME_MAX_WIDTH } from '@/tenants/walmart/theme/layout'
import actionClasses from '@/tenants/walmart/theme/actions.module.css'
import type { LegalTextSegment } from '@/tenants/walmart/content/legal'
import { useUserContext } from '@/contexts/UserContext'
import { isAuthenticatedViaMiddlewareCookie } from '@/lib/isAuthenticatedViaMiddlewareCookie'

interface TopBarProps {
    productName: string
    coordinatorPrefix: string
    coordinatorName: string
    signInLabel: string
    signInHref: string
    /** Read on the server; only used until the client can read auth itself. */
    signedIn: boolean
    accountLabel: string
    accountHref: string
    coordinatorDialog: {
        title: string
        items: readonly (readonly LegalTextSegment[])[]
        dismissLabel: string
    }
}

const noSubscription = () => () => {}

/** Visit detail is reached from the account, so its brand link leads back there. */
const ACCOUNT_HOME_PATHNAME = /^\/visits\/[^/]+\/?$/

/**
 * The root layout never re-renders on client navigation, so its server-read sign-in state goes stale
 * after a login or logout. Re-reading the middleware cookie on each pathname change keeps the bar current.
 */
function useSignedIn(serverSignedIn: boolean): boolean {
    const { user } = useUserContext()
    usePathname()
    const cookieSignedIn = useSyncExternalStore(
        noSubscription,
        isAuthenticatedViaMiddlewareCookie,
        () => serverSignedIn,
    )
    return Boolean(user) || cookieSignedIn
}

/** Exposes the sticky bar's height on the root so fixed elements elsewhere can sit below it. */
function usePublishedHeight(property: string) {
    const ref = useRef<HTMLElement>(null)
    useEffect(() => {
        const element = ref.current
        if (!element) return
        const root = document.documentElement
        const observer = new ResizeObserver(() => {
            root.style.setProperty(property, `${element.offsetHeight}px`)
        })
        observer.observe(element)
        return () => {
            observer.disconnect()
            root.style.removeProperty(property)
        }
    }, [property])
    return ref
}

export default function TopBar({
    productName,
    coordinatorPrefix,
    coordinatorName,
    signInLabel,
    signInHref,
    signedIn,
    accountLabel,
    accountHref,
    coordinatorDialog,
}: TopBarProps) {
    const [dialogOpened, { open: openDialog, close: closeDialog }] = useDisclosure(false)
    const isSignedIn = useSignedIn(signedIn)
    const homeHref = ACCOUNT_HOME_PATHNAME.test(usePathname() ?? '') ? accountHref : '/'
    const headerRef = usePublishedHeight('--walmart-top-bar-height')

    return (
        <Box
            ref={headerRef}
            component="header"
            className={classes.topBar}
            bg="var(--walmart-surface-accent-teal)"
            py={{ base: 4, md: 0 }}
        >
            <Group
                gap="md"
                align="center"
                wrap="nowrap"
                className={classes.content}
                __vars={{ '--walmart-top-bar-content-max-width': `${WALMART_CHROME_MAX_WIDTH}px` }}
                mx="auto"
                px={{ base: 'md', md: 24 }}
                h={{ md: 80 }}
            >
                <Stack gap={0} flex={1} miw={0}>
                    {/* Branding, not a heading — a heading here would precede the page's h1. */}
                    <Anchor
                        href={homeHref}
                        underline="never"
                        c="var(--gm-foreground-fg-primary)"
                        data-testid="walmart-top-bar-home"
                    >
                        <Text
                            fz={{ base: 'xl', md: '2xl' }}
                            lh={{ base: 'xl', md: 'var(--gm-text-2xl-line-height)' }}
                            fw={700}
                            inherit
                        >
                            {productName}
                        </Text>
                    </Anchor>
                    <Text
                        fz={{ base: 'xs', md: 'sm' }}
                        lh={{ base: 'xs', md: 'sm' }}
                        c={{ base: 'var(--gm-foreground-fg-primary)', md: 'var(--gm-gray-6)' }}
                    >
                        {`${coordinatorPrefix} `}
                        <UnstyledButton
                            onClick={openDialog}
                            fz="inherit"
                            lh="inherit"
                            fw={600}
                            c="inherit"
                            td="underline"
                            data-testid="walmart-top-bar-coordinator"
                        >
                            {coordinatorName}
                        </UnstyledButton>
                    </Text>
                </Stack>
                {isSignedIn ? (
                    <Anchor href={accountHref} aria-label={accountLabel} data-testid="walmart-top-bar-account">
                        <ThemeIcon
                            radius="var(--gm-radius-round)"
                            size={40}
                            bg="var(--walmart-action-fill-strong)"
                            c="var(--walmart-action-text-on-strong)"
                        >
                            <IconUser aria-hidden size={22} stroke={1.5} />
                        </ThemeIcon>
                    </Anchor>
                ) : (
                    <Anchor
                        href={signInHref}
                        w="auto"
                        flex="0 0 auto"
                        className={`${actionClasses.filledAction} ${classes.signIn}`}
                        data-testid="walmart-top-bar-sign-in"
                    >
                        {signInLabel}
                    </Anchor>
                )}
            </Group>

            <CoordinatorDialog
                opened={dialogOpened}
                onClose={closeDialog}
                title={coordinatorDialog.title}
                items={coordinatorDialog.items}
                dismissLabel={coordinatorDialog.dismissLabel}
            />
        </Box>
    )
}
